import {
  GridModalFormProps,
  Option,
  UseFormReturnType,
  Validator,
  enumToOptions,
  globalMessageHandler,
  useForm,
} from "common";
import { useParams } from "common/components/Form/ParamsContext";
import { Dictionary } from "features/dictionaries/dictionaryApi";
import useDictionaries from "features/dictionaries/useDictionaries";
import {
  EventItem,
  getEventService,
  updateBoatLowCancelled,
} from "features/events/eventApi";
import { EVENT_STATUSES } from "features/events/eventConstants";
import { ProgramVariantItem } from "features/program-variants/programVariantApi";
import { ProgramItem } from "features/programs/programApi";
import { LIMIT_TYPES } from "features/schedules/scheduleConstants";
import { validateEvent } from "helpers/validationHelper";

import I18n from "i18n";
import { useEffect, useMemo } from "react";
import { Params } from "react-router-dom";

export interface UseEventCalendarFormProps extends GridModalFormProps {}

type RouteParams = {
  event_id?: string;
};

export interface UseEventCalendarFormReturnType extends Omit<
  UseFormReturnType<EventItem>,
  "options"
> {
  params?: Readonly<
    Params<
      | "event_id"
      | "boat_id"
      | "program_id"
      | "schedule_id"
      | "booking_id"
      | "booking_option_id"
    >
  >;
  program?: ProgramItem;
  programs?: ProgramItem[];
  groupVariants?: ProgramVariantItem[];
  options: Dictionary;
  type: Option[];
  toggleBoatLowCancelled: () => void;
}

export default function useEventCalendarForm({
  id,
  afterSave,
  defaultValues,
}: UseEventCalendarFormProps): UseEventCalendarFormReturnType {
  const params = useParams<
    | "event_id"
    | "boat_id"
    | "program_id"
    | "schedule_id"
    | "booking_id"
    | "booking_option_id"
  >();
  const scope = {
    boats: params.boat_id,
    programs: params.program_id,
    schedules: params.schedule_id,
  };
  const service = getEventService(scope);

  const form = useForm<EventItem>({
    id: params.event_id || id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "Event",
    rules: {
      start_time: Validator.rule.any().required(),
      boat_id: params.boat_id
        ? Validator.rule.optional()
        : Validator.rule.any().required(),
      program_id: params.program_id
        ? Validator.rule.optional()
        : Validator.rule.any().required(),
      duration: Validator.rule.any().required(),
      status: Validator.rule.any().required(),
      variants: Validator.rule.any().required(),
      limits: Validator.rule.any().required(),
    },
    afterSave,
    defaultValues,
    onValidationFailed: () => {
      globalMessageHandler.snack({
        severity: "error",
        message: I18n.t("App.formHasErrors"),
      });
    },
    ...service,
    //loadItem: undefined,
    loadOptions: undefined,
    customValidation(newItem) {
      return customValidation(newItem);
    },
  });
  const { dictionaries, resolveOptions, filterOptions, resolveOption } =
    useDictionaries();

  const { options, program, programs, type, hasOptions, groupVariants } =
    useMemo(() => {
      const prigramIds = [form.item.program_id];
      const programs = resolveOptions("programs", prigramIds);
      const program = resolveOption<ProgramItem>(
        "programs",
        form.item.program_id,
      );
      return {
        options: {
          ...dictionaries,
          boat_id: dictionaries.boats,
          program_id: dictionaries.programs,
          status: enumToOptions(EVENT_STATUSES, "Event.options.status"),
          variants: filterOptions<ProgramVariantItem>("variants", item =>
            prigramIds?.includes(item.program_id),
          ),
        } as any,
        groupVariants: resolveOptions<ProgramVariantItem>(
          "variants",
          form.item.group_variant_ids,
        ),
        hasOptions: !!programs?.find(p => p?.has_options),
        program,
        programs,
        type: enumToOptions(LIMIT_TYPES, "Limit.options.type"),
      };
    }, [form.item.program_id]);

  function handleCalendar({
    detail: { action, item },
  }: {
    detail: { action: "refresh" | "setItem"; item?: any };
  }) {
    if (action === "refresh") {
      if (item) {
        form.setAttributes(item);
      } else {
        form.load();
      }
    }
    if (action === "setItem") {
      form.setAttributes(item);
    }
  }
  useEffect(() => {
    //@ts-ignore
    document.addEventListener("calendarAction", handleCalendar);
    return () =>
      //@ts-ignore
      document.removeEventListener("calendarAction", handleCalendar);
  }, []);

  function customValidation(newItem: EventItem) {
    const customErrors = validateEvent(
      newItem.limits,
      newItem.variants,
      newItem.options,
      programs,
      options?.variants,
      hasOptions,
    );

    let errors: any = {};
    if (customErrors.limits) errors.limits = customErrors.limits;
    if (customErrors.variants) errors.variants = customErrors.variants;
    if (customErrors.options) errors.options = customErrors.options;

    return Object.keys(errors).length > 0 ? errors : undefined;
  }

  async function toggleBoatLowCancelled() {
    const isCancelled = form.item.boat_low_cancelled;
    globalMessageHandler.confirm(
      {
        title: isCancelled
          ? I18n.t("Event.boatLowCancelledUndoTitle")
          : I18n.t("Event.boatLowCancelledTitle"),
        message: isCancelled
          ? I18n.t("Event.boatLowCancelledUndoMessage")
          : I18n.t("Event.boatLowCancelledMessage"),
      },
      async () => {
        const response = await updateBoatLowCancelled(
          form.item.event_group_id,
          !isCancelled,
        );
        if (response.success) {
          form.setAttributes({
            ...form.item,
            boat_low_cancelled: !isCancelled,
          });
          afterSave &&
            afterSave(
              { ...form.item, boat_low_cancelled: !isCancelled },
              false,
            );

          globalMessageHandler.snack({
            severity: "success",
            message: isCancelled
              ? I18n.t("Event.boatLowCancelledUndoSuccess")
              : I18n.t("Event.boatLowCancelledSuccess"),
          });
        } else {
          globalMessageHandler.snack({
            severity: "error",
            message: isCancelled
              ? I18n.t("Event.boatLowCancelledUndoError")
              : I18n.t("Event.boatLowCancelledError"),
          });
        }
      },
    );
  }

  let rules: any = {};

  if (hasOptions) {
    rules.options = Validator.rule.any().required();
  }
  form.addRules(rules);

  return {
    ...form,
    options,
    program,
    params,
    programs,
    type,
    groupVariants,
    toggleBoatLowCancelled,
  };
}
